/**
 * Page Navigation Component
 * Provides page controls for the PDF viewer including
 * prev/next buttons, page input, and quick jump bar for pages with findings
 */

import { useState, useCallback, useEffect, KeyboardEvent } from 'react';
import { ChevronLeft, ChevronRight, FileText } from 'lucide-react';

interface PageNavigationProps {
  /** Current page number (1-indexed) */
  currentPage: number;
  /** Total number of pages in the document */
  totalPages: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Array of page numbers that have findings (highlighted in quick jump bar) */
  pagesWithFindings?: number[];
}

export function PageNavigation({
  currentPage,
  totalPages,
  onPageChange,
  pagesWithFindings = [],
}: PageNavigationProps) {
  const [inputValue, setInputValue] = useState<string>(String(currentPage));

  // Sync input value when currentPage changes externally
  useEffect(() => {
    setInputValue(String(currentPage));
  }, [currentPage]);

  const goToPrevious = useCallback(() => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  }, [currentPage, onPageChange]);

  const goToNext = useCallback(() => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, onPageChange]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const handleInputBlur = useCallback(() => {
    const page = parseInt(inputValue, 10);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      onPageChange(page);
    } else {
      // Reset to current page if invalid
      setInputValue(String(currentPage));
    }
  }, [inputValue, totalPages, currentPage, onPageChange]);

  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleInputBlur();
      } else if (e.key === 'Escape') {
        setInputValue(String(currentPage));
        (e.target as HTMLInputElement).blur();
      }
    },
    [handleInputBlur, currentPage]
  );

  // Keyboard navigation for arrow keys
  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      // Only handle if no input is focused
      if (document.activeElement?.tagName === 'INPUT') return;

      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevious, goToNext]);

  // Determine which pages to show in quick jump (limit to avoid overflow)
  const maxQuickJumpPages = 10;
  const showAllPages = totalPages <= maxQuickJumpPages;
  const quickJumpPages = showAllPages
    ? Array.from({ length: totalPages }, (_, i) => i + 1)
    : pagesWithFindings.slice(0, maxQuickJumpPages);

  return (
    <div className="flex flex-col gap-2">
      {/* Main navigation controls */}
      <div className="flex items-center justify-between gap-4">
        {/* Previous button */}
        <button
          onClick={goToPrevious}
          disabled={currentPage <= 1}
          className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Previous page"
        >
          <ChevronLeft size={18} />
          <span className="hidden sm:inline">Previous</span>
        </button>

        {/* Page indicator with input */}
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <span>Page</span>
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            className="w-12 px-2 py-1 text-center text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            aria-label="Current page number"
          />
          <span>of {totalPages}</span>
        </div>

        {/* Next button */}
        <button
          onClick={goToNext}
          disabled={currentPage >= totalPages}
          className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] sm:min-h-0"
          aria-label="Next page"
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Quick jump bar for pages with findings */}
      {pagesWithFindings.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <FileText size={12} />
            Pages with findings:
          </span>
          <div className="flex items-center gap-1 flex-wrap">
            {quickJumpPages.map((page) => {
              const hasFinding = pagesWithFindings.includes(page);
              const isActive = page === currentPage;

              return (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`
                    min-w-[28px] h-7 px-2 text-xs font-medium rounded transition-colors
                    ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : hasFinding
                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/40 border border-amber-300 dark:border-amber-700'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }
                  `}
                  title={`Go to page ${page}${hasFinding ? ' (has findings)' : ''}`}
                >
                  {page}
                </button>
              );
            })}
            {!showAllPages && pagesWithFindings.length > maxQuickJumpPages && (
              <span className="text-xs text-gray-400 dark:text-gray-500">
                +{pagesWithFindings.length - maxQuickJumpPages} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PageNavigation;
